import { getPreferredPhone, useGetAllContacts } from "@api/contactService";
import { useCallback, useEffect, useRef, useState } from "react";
import Box from '@mui/joy/Box';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import Diversity3RoundedIcon from '@mui/icons-material/Diversity3Rounded';
import WalletRoundedIcon from '@mui/icons-material/WalletRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useGetAllUsers } from "@api/userService";
import { Alert, Autocomplete, AutocompleteOption, Button, Checkbox, Grid, ListItemContent, ListItemDecorator, Option, Select, Sheet, Stack, Step, StepButton, StepIndicator, Stepper, Table, Textarea } from "@mui/joy";
import { Link } from "react-router-dom";
import { ContextualHelp } from "@components/ContextualHelp";
import { countries, userRoles } from "@utils/selectOptions";
import Subheader from "@components/layout/Subheader";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { ObjectPrimaryInfoEdit } from "@components/objects/ObjectPrimaryInfoEdit";
import ObjectColumn from "@components/objects/ObjectColumn";
import { useTenant } from "@providers/TenantProvider";
import { apiUrl, authenticatedApi, getAccessToken } from "api";
import toast from "react-hot-toast";
import { useGetAllCoordinatingPrograms } from "@api/coordinatingProgramService";
import MaestroLoader from "@components/MaestroLoader";
import { formatDate, formatDateForDatabase } from "@utils/dates";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import { useCopyToClipboard, useDebounce, useDocumentTitle } from "@uidotdev/usehooks";
import Uploady, {
    useUploady,
    useBatchAddListener,
    useItemFinalizeListener,
    useItemProgressListener,
    useAbortItem,
    useItemFinishListener,
    useFileInput,
    FILE_STATES,
    useBatchErrorListener,
} from "@rpldy/uploady";
import UploadButton from "@rpldy/upload-button";
import UploadDropZone from "@rpldy/upload-drop-zone";
import Papa from 'papaparse';
import { importableColumns } from "@utils/importableColumns";
import { formatNumber } from "@utils/numbers";
import SearchSelect from "@components/SearchSelect";
import { events } from "@utils/analytics";

const importTypes = [
    // Primary categories
    {
        key: 'contacts',
        name: 'Contacts',
        icon: PeopleRoundedIcon,
    },
    {
        key: 'businesses',
        name: 'Businesses',
        icon: StoreRoundedIcon,
    },
    {
        key: 'properties',
        name: 'Properties',
        icon: ApartmentRoundedIcon,
    },
    {
        key: 'workplans',
        name: 'Work Plan',
        icon: ChecklistRoundedIcon,
    },

    /*
    // Secondary categories
    {
        key: 'donations',
        name: 'Donations',
        icon: 'wallet',
    },
    {
        key: 'volunteer-hours',
        name: 'Volunteer Hours',
        icon: 'man-woman',
    },
    {
        key: 'involvements',
        name: 'Involvements (Advanced)',
        icon: 'tags',
    },
    // Appeals, Campaigns, Funds, Grants, Membership Levels, Public Improvements, Public Investments, Registries, SSAs/BIDs, TIFs, Trainings, Zoning
    */
];

const steps = [
    {
        name: 'Import Type',
    },
    /*
    // this is only relevant if we allow something other than .csv
    {
        name: 'Import Method',
    },
    */
    {
        name: 'Choose Data',// 'Upload Data',
    },
    {
        name: 'Confirm Data',
    },
    {
        name: 'Done',
    },
];

export default function ImportCreate() {
    return (
        <Uploady>
            <ImportCreateInner/>
        </Uploady>
    );
}

function ImportCreateInner() {
    useDocumentTitle(`Import Data | Maestro Community Manager`);
    // const { tenant, setTenant } = useTenant();

    const uploady = useUploady();
    const [fileTypeError, setFileTypeError] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    const [importType, setImportType] = useState(null);
    const [activeStep, setActiveStep] = useState(0);

    const [processingFile, setProcessingFile] = useState(false);
    const [totalLines, setTotalLines] = useState(0);
    const [previewRows, setPreviewRows] = useState([]);
    const [importPossibleColumns, setImportPossibleColumns] = useState({});
    const [columns, setColumns] = useState(null);
    const [additionalInformation, setAdditionalInformation] = useState(null);
    const [selectedInvolvements, setSelectedInvolvements] = useState([]);
    const [columnsCorrect, setColumnsCorrect] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [importId, setImportId] = useState(0);

    const setAdditionalInformationForColumn = (columnIndex, newValue) => {
        let _additionalInformation = additionalInformation === null ? {columns:{}} : {...additionalInformation};
        _additionalInformation.columns[columnIndex] = newValue;
        setAdditionalInformation(_additionalInformation);
    }

    const unmatchedColumns = columns?.filter(column => column === false);
    const ignoredColumns = columns?.filter(column => column === '--ignore--');

    const chooseImportType = (type) => {
        setImportType(type);
        setActiveStep(1);
    };

    /*
    |--------------------------------------------------------------------------
    | File Picker
    |--------------------------------------------------------------------------
    */
    const fileFilter = useCallback((file) => {
        const isValid = file.type.indexOf('text/csv') !== -1;
        setFileTypeError(!isValid);
        if(!isValid/* && selectedFile*/) {
            setSelectedFile(null);
        }

        return isValid;
    }, []);

    const uploadyOptions = {
        grouped: true,
        maxGroupSize: 1,
        fileFilter: fileFilter,
        autoUpload: false,
        clearPendingOnAdd: true,
    };

    const showFileUploader = () => {
        uploady.showFileUpload(uploadyOptions);
    }

    useBatchAddListener((batch) => {
        setSelectedFile(batch.items[0].file);
    });

    /*
    |--------------------------------------------------------------------------
    | CSV Parser / Matcher
    |--------------------------------------------------------------------------
    */
    useEffect(() => {
        if(!selectedFile) {
            return;
        }

        setProcessingFile(true);
        setActiveStep(2);

        // ensure we reset data in case they went back to upload a new file
        setTotalLines(0);
        setImportPossibleColumns(getUsableColumns());
        setColumns(null);
        setAdditionalInformation(null);
        setSelectedInvolvements([]);

        let _previewRows = [];
        let _totalLines = 0;

        Papa.parse(selectedFile, {
            skipEmptyLines: true,
            step: function(result, parser)
            {
                _totalLines++;

                if(_totalLines <= 6)
                {
                    _previewRows.push(result.data);

                    /*
                    if(_totalLines > 0)
                    {
                        refreshColumns();
                    }
                    */
                }
            },
            error: function(err, file, inputElem, reason)
            {
                // todo: go back a step & let them choose a new file
                toast.error('There was an error while processing your file. Please contact Maestro support.');
            },
            complete: function(results, file) {
                setPreviewRows(_previewRows);
                setTotalLines(_totalLines);
                autodetectColumns(_previewRows);

                setTimeout(() => {
                    setProcessingFile(false);
                    // setActiveStep(2);
                }, 150);
            }
        });
    }, [selectedFile]);


    const getUsableColumns = function() {
        let columns = {
            '--ignore--': {name:'(Ignore Column)', variations: []},
            ...importableColumns[importType],
        };

        // If we're not importing work plans, allow importing involvements
        if(importType != 'workplans') {
            columns['involvement'] = {
                name: 'Involvement (Name)',
                variations: ['involvement_name']
            };

            columns['involvement_date'] = {
                name: 'Involvement (Data)',
                variations: ['involvement_data']
            };

            columns['involvement_notes'] = {
                name: 'Involvement (Notes)',
                variations: ['involvement_note']
            };
        }

        return columns;
    };

    const autodetectColumns = function(_previewRows) {
        // Set the suggested file columns
        var tmpColumns = [];
        if(_previewRows && _previewRows.length)
        {
            for(var columnKey in _previewRows[0])
            {
                tmpColumns.push(guessColumnHeader(_previewRows[0][columnKey]));
            }
        }

        setColumns(tmpColumns);
    };

    const guessColumnHeader = function(input) {
        input = input.trim();
        var normalizedName = input.replace(/ +/g, '_').replace(/[^0-9a-z_]/gi, '').replace(/_+/g, '_').toLowerCase();

        var usableColumns = importableColumns[importType];
        // var friendlyNames = importableColumns[importType];

        input = input.toLowerCase(); // force lowercase compare

        // Ignore blacklist
        if(input == 'id' || input.match(/_id$/gi) || input.match(/_at$/gi))
        {
            return '--ignore--';
        }

        // Search for direct match
        if(usableColumns.hasOwnProperty(input))
        {
            return input;
        }

        // Search for "direct" match (normalized name)
        if(usableColumns.hasOwnProperty(normalizedName))
        {
            return normalizedName;
        }

        for(var columnKey in usableColumns)
        {
            var column = usableColumns[columnKey];

            // Don't proceed if the column doesn't have alternate names
            if(!column.variations.length)
            {
                continue;
            }

            // Search for direct match of alternate names
            if(column.variations.indexOf(input) != -1 || column.variations.indexOf(normalizedName) != -1)
            {
                return columnKey;
            }
        }

        // console.log("no match found for "+normalizedName);

        // It didn't match
        return false;//'--ignore--';
    };

    const columnsHaveSubSelection = function() {
        for(var columnIterator in columns)
        {
            var column = columns[columnIterator];
            // Todo: make this dynamic by putting an optional bool inside of possibleColumns
            if(typeof column === 'string' && column.match('involvement'))
            {
                return true;
            }
        }

        return false;
    };

    const getSelectedInvolvements = function()
    {
        var involvementIds = [];

        for(var columnIterator in columns)
        {
            if(columns[columnIterator] == 'involvement'
               && additionalInformation != null
               && additionalInformation.hasOwnProperty('columns')
               && additionalInformation.columns.hasOwnProperty(columnIterator)
            )
            {
                involvementIds.push(additionalInformation.columns[columnIterator]);
            }
        }

        setSelectedInvolvements(involvementIds);
        return involvementIds;
    };

    const hasUnselectedInvolvements = function() {
        for(var columnIterator in columns) {
            if(
                columns[columnIterator] == 'involvement'
                && (
                    additionalInformation == null
                    || !additionalInformation.hasOwnProperty('columns')
                    || !additionalInformation.columns.hasOwnProperty(columnIterator)
                )
            ) {
                return true;
            }
        }

        return false;
    };

    const hasDuplicatedInvolvements = function() {
        var involvementIds = {};

        for(var columnIterator in columns)
        {
            if(
                columns[columnIterator] == 'involvement'
                && additionalInformation != null
                && additionalInformation.hasOwnProperty('columns')
                && additionalInformation.columns.hasOwnProperty(columnIterator)
            ) {
                var involvementId = additionalInformation.columns[columnIterator];
                if(involvementIds.hasOwnProperty(involvementId))
                {
                    return true;
                }

                involvementIds[involvementId] = involvementId;
            }
        }

        return false;
    };

    const isFilterAlreadyAdded = function(item) {
        return item != '--ignore--' && !item.match('involvement') && columns.indexOf(item) != -1;
    };

    const formMethods = useForm({
        defaultValues: {
            has_headers: 1,
        },
    });
    const {
        setValue,
        reset,
        handleSubmit,
        getValues,
        watch,
        register,
        control,
        formState: { isValid, isSubmitting, errors }
    } = formMethods;

    const stepDisabled = (index) => {
        // do not allow paginating while we are saving
        if(isSaving) {
            return true;
        }

        // don't allow paginating back from the final step
        if(activeStep === 3) {
            return true;
        }

        // upload data
        if(index === 1) {
            // if work plan, ensure there's a work plan name
            if(importType === 'workplans') {
                //!isValid
                //watch('name')
            }

            // ensure there's an import type
            if(importType !== null) {
                return false;
            }
        }

        if(index === 2) {
            return fileTypeError || !selectedFile;
        }

        return index > activeStep;
    }


    const onSubmit = (fields) => {
        // Don't allow importing if our involvements aren't set up correctly
        if(hasUnselectedInvolvements() || hasDuplicatedInvolvements())
        {
            return;
        }

        setIsSaving(true);


        let requestFields = {
            id: null,
            status: 'action-required', // the API sets to queued after the file uploads
            type: importType,
            has_headers: fields.has_headers, // whether the CSV has headers or not

            // unused fields (possibly required by the api though)
            include_properties: 0, // If businesses, allow users to also create properties
            mode: 'csv', // csv, copy-paste, integrated-service

            // Info to upload
            import_text: null,

            // Columns to process
            columns: columns,
            additional_information: additionalInformation,
        };

        if(importType === 'workplans') {
            requestFields.import_text = {
                name: fields.name,
                workplan_date: fields.workplan_date,
                description: fields.description,
            };
        }

        let formData = new FormData();
        for (var key in requestFields) {
            if(typeof requestFields[key] === 'object') {
                formData.append(key, JSON.stringify(requestFields[key]));
            } else {
                formData.append(key, requestFields[key]);
            }
        }

        formData.append('file', selectedFile);

        const performSave = fetch(`${apiUrl}imports`, {
            method: 'post',
            headers: {
                'Authorization': `Bearer ${getAccessToken()}`,
                'Accept': 'application/json',
                // 'Content-Type': 'application/json',
            },
            body: formData,
        });

        toast.promise(performSave, {
            loading: 'Uploading... (This could take a minute)',
            success: `The data was uploaded and will be processed soon.`,
            error: 'There was an error uploading the data.',
        });

        performSave.then(response => response.json()).then(response => {
            events.dataImported();

            // console.log(response);
            setImportId(response.id);
            setActiveStep(3);
        });

        performSave.finally(() => {
            setIsSaving(false);
        });
    };

    return (<>
        <Subheader
            title={'Import Data'}
            tint={'default'}
        >
        </Subheader>

        <Box sx={theme => ({
            // backgroundImage: `linear-gradient(180deg, ${theme.palette['default'][100]} 0%, white 100%);`,// : null,
            backgroundAttachment: 'fixed',
            minHeight: 'calc(100% - var(--Header-height))',
            p: 2,
        })}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <FormProvider {...formMethods}>
                <AbstractObjectRelationship
                    title={<>&nbsp;</>}
                    sx={{
                        '--mm-card-height': 'auto',
                    }}
                >
                    <Box p={2}>
                        <Stack direction={'row'} sx={{mb: 2, alignItems: 'center'}}>
                        <IconButton
                            variant="solid"
                            disabled={activeStep === 0 || activeStep === 3 || isSaving}
                            onClick={() => {
                                setActiveStep(Math.max(0, activeStep - 1));
                            }}
                        >
                            <ArrowBackRoundedIcon/>
                        </IconButton>

                        <Stepper
                            sx={{
                                width: '100%',
                                "--Step-connectorThickness": "5px",
                                "--Step-connectorInset": "10px",
                                "--Step-connectorRadius": "20px",
                                "--StepIndicator-size": "48px",

                                '.MuiStep-root.Mui-disabled': {
                                    cursor: 'not-allowed',
                                    pointerEvents: 'none',
                                },

                                /*
                                '.MuiStepButton-root[disabled]': {
                                    cursor: 'not-allowed',
                                    pointerEvents: 'none',
                                }
                                */
                            }}
                        >
                        {steps.map((step, index) => (
                            <Step
                                key={step.name}
                                orientation="vertical"
                                size="lg"
                                disabled={stepDisabled(index)}
                                indicator={
                                    <StepIndicator
                                    variant={(activeStep <= index && activeStep < 3) ? 'soft' : 'solid'}
                                    color={(activeStep < index && activeStep <3) ? 'neutral' : 'primary'}
                                    >
                                    {(activeStep <= index && activeStep < 3) ? index + 1 : <CheckRoundedIcon />}
                                    </StepIndicator>
                                }
                            >
                            <StepButton onClick={() => setActiveStep(index)}>{step.name}</StepButton>
                            </Step>
                        ))}
                        </Stepper>

                        <IconButton
                            variant="solid"
                            disabled={stepDisabled(activeStep+1)}
                            onClick={() => {
                                setActiveStep(activeStep + 1);
                            }}
                        >
                            <ArrowForwardRoundedIcon/>
                        </IconButton>
                        </Stack>

                        {activeStep === 0 && (
                            <Grid container columns={12} sx={{gap: {xs: 0.5, sm: 2}}}>
                                <Grid xs md={6} lg={8} sx={{paddingY: 1}}>
                                    <Typography sx={{fontSize: 'lg', fontWeight: 'bold', mb: 0.5}}>What type of data are you importing?</Typography>
                                    {/* <Typography><b>Note:</b> If you haven't imported/created any properties yet, it might be easier to create your properties based on your business addresses. Check the "businesses" option to do this.</Typography> */}

                                    <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2
                                    }}
                                    >
                                    {importTypes.map(type => (
                                        <Sheet
                                            key={type.key}
                                            variant="solid"
                                            color={type.key === importType ? 'primary' : 'neutral'}
                                            onClick={()=>chooseImportType(type.key)}
                                            sx={{
                                                borderRadius: 'sm',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                p: 2,
                                                flex: 1,
                                                cursor: 'pointer',
                                            }}

                                        >
                                            <type.icon sx={{fontSize: '3rem'}}/>
                                            {type.name}
                                        </Sheet>
                                    ))}
                                    </Box>
                                </Grid>
                                <Grid xs>
                                    <Typography sx={{fontSize: 'lg', fontWeight: 'bold', mb: 0.5}}>Importing Tips</Typography>
                                    <Typography component={"div"}>
                                        In order to ensure that all your connections are made upon import (Contacts to Businesses, Contacts/Business to Properties, etc), we suggest importing your files in the following order:
                                        <ul>
                                            <li>Contacts</li>
                                            <li>Businesses</li>
                                            <li>Properties</li>
                                        </ul>
                                        {/* Importing is an intricite process and we highly recommend reading up before hitting the button. */}
                                    </Typography>
                                    <Button
                                        component={'a'}
                                        href="https://docs.maestrocm.com/books/getting-started/page/inputting-data#bkmrk-importing-data"
                                        target="_blank"
                                        startDecorator={<HelpOutlineRoundedIcon/>}
                                    >
                                        Read Importing Documentation
                                    </Button>
                                </Grid>
                            </Grid>
                        )}

                        {activeStep === 1 && (<>
                            {importType === 'workplans' && (<>
                            <Typography sx={{fontSize: 'lg', fontWeight: 'bold', mb: 0.5}}>1. Enter work plan information</Typography>
                            {/* <Labl */}
                            <FormControl sx={{mb: 2, maxWidth: '450px'}}>
                                <FormLabel>Name</FormLabel>
                                <Input
                                    placeholder="e.g. 2024 Clean Up Day"
                                    {...register('name', {required: true})}
                                />
                            </FormControl>
                            <FormControl sx={{mb: 2, maxWidth: '450px'}}>
                                <FormLabel>Date (Optional)</FormLabel>
                                <Input
                                    placeholder="e.g. 05/23/2016"
                                    type={'date'}
                                    {...register('workplan_date')}
                                />
                            </FormControl>
                            <FormControl sx={{mb: 2, maxWidth: '450px'}}>
                                <FormLabel>Description (Optional)</FormLabel>
                                <Textarea
                                    placeholder="Enter a description..."
                                    {...register('description')}
                                    minRows={3}
                                />
                            </FormControl>

                            <Typography sx={{fontSize: 'lg', fontWeight: 'bold', mb: 0.5}}>2. Choose your data</Typography>
                            <Typography>
                            <b>Note:</b> Because there are nearly infinite different ways to format work plans, you will need to use our work plan template to import them.
                            <br/>
                            <a href="/files/importers/templates/maestro_workplans_template.csv" target="_blank">Click here</a> to download the template. An example file is also available <a href="/files/importers/examples/maestro_workplans_example.csv" target="_blank">viewable here</a>.
                            </Typography>
                            </>)}
                            {importType !== 'workplans' && (<>
                            <Typography sx={{fontSize: 'lg', fontWeight: 'bold', mb: 0.5}}>Choose your data</Typography>
                            <Typography>
                                If you have an <b>.xls</b> or <b>.xlsx</b> file, you will need to convert it to a <b>.csv</b> file. (Renaming it will not work.)
                                <br/>
                                We can import most <b>.csv</b> files. You can also use our <a href={`/files/importers/templates/maestro_${importType}_template.csv`} target="_blank">{importType} <b>.csv</b> template.</a>
                            </Typography>
                            </>)}

                            <UploadDropZone
                                className="file-upload-dnd"
                                onDragOverClassName="drag-over"
                                {...uploadyOptions}
                                /*
                                grouped
                                maxGroupSize={1}
                                fileFilter={fileFilter}
                                autoUpload={false}
                                clearPendingOnAdd={true}
                                */
                            >
                            <div onClick={showFileUploader}>
                                {selectedFile !== null && (<>
                                    <Typography sx={{fontSize: 'lg', fontWeight: 'bold', mb: 0.5}}>Selected file</Typography>
                                    <Typography sx={{mb: 2}}>{selectedFile.name}</Typography>
                                </>)}

                                <Typography>
                                    Drag and drop your{importType === 'workplans' ? ' work plan' : ''} <b>.csv</b> file here.
                                    <br/>
                                    (Or click here to choose your file)
                                </Typography>

                                {/*
                                <div class="uploader text-center" nv-file-drop nv-file-over over-class="active" uploader="uploader" filters="csvOnly" ng-click="triggerFileChooser()">
                                    <div ng-if="fileName">
                                        <h3 class="m-t-none m-b-xs">Selected file</h3>
                                        <p><i class="icon-file-spreadsheet"></i> {{ fileName }}</p>
                                        <!--<h3>Choose new file</h3>-->
                                    </div>

                                </div>
                                */}
                                {fileTypeError === true && (
                                    <Typography color="danger" sx={{mt: 2}}>Only{importType === 'workplans' ? ' work plan' : ''} <b>.csv</b> files can be uploaded. Please try a different file.</Typography>
                                )}
                                </div>
                            </UploadDropZone>
                        </>)}

                        {activeStep === 2 && processingFile === true && (<><MaestroLoader/></>)}
                        {activeStep === 2 && processingFile !== true && (<>
                            <Typography sx={{fontSize: 'lg', fontWeight: 'bold', mb: 0.5}}>Confirm the data to be imported</Typography>
                            <Typography>
                                Please review the data you wish to import to ensure that your columns were detected correctly. Although only a handful of rows are displayed below, all rows will be imported.
                            </Typography>

                            {importType === 'workplans' && (<>
                                <Typography sx={{fontSize: 'lg', fontWeight: 'bold', mt: 2, mb: 0.5}}>Work Plan Information</Typography>
                                <Typography>
                                    <b>Name:</b> {getValues('name')}
                                    <br/>
                                    <b>Date:</b> {formatDate(getValues('workplan_date'))}
                                    <br/>
                                    <b>Description:</b> {getValues('description')}
                                </Typography>
                                <Typography sx={{fontSize: 'lg', fontWeight: 'bold', mt: 2, mb: 0.5}}>File Information</Typography>
                            </>)}

                            <Typography>
                            To change an incorrect column, use the drop downs at the top of the table below. Ignored and unmatched columns will not be imported.
                            </Typography>

                            {(hasDuplicatedInvolvements() === true || unmatchedColumns.length > 0 || ignoredColumns.length > 0) && (
                                <Alert
                                    color="warning"
                                    variant="soft"
                                    sx={{my: 2}}
                                >
                                    <>
                                    {unmatchedColumns.length > 0 && (<>
                                        <b>Unmatched Columns: {unmatchedColumns.length}</b>
                                        <br/>
                                    </>)}
                                    {ignoredColumns.length > 0 && (<>
                                        <b>Ignored Columns: {ignoredColumns.length}</b>
                                        <br/>
                                    </>)}
                                    {/* TODO: display message for incorrectly configured involvements */}
                                    {hasDuplicatedInvolvements() === true && (<div>
                                        <b>Note:</b> You cannot use the same involvement in multiple columns. Please use duplicate rows instead. See our tutorial for more information.
                                    </div>)}
                                    </>
                                </Alert>
                            )}

                            <Sheet sx={{overflowX: 'auto', borderRadius: 0}}>
                            <Table
                            stripe={'odd'}
                            sx={{
                                tableLayout: 'auto',
                                'th': {
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                },
                                'thead tr + tr th': {
                                    fontWeight: 'normal',
                                }
                            }}
                            >
                            <thead>
                            <tr>
                                {previewRows?.[0].map((column, columnIndex) => (
                                    <th
                                        key={columnIndex}
                                        className={columns[columnIndex] === false ? 'import-unmatched' : ''}
                                        style={{width: 'auto'}}
                                    >
                                    {column}
                                    </th>
                                ))}
                            </tr>
                            <tr>
                            {previewRows?.[0]?.map((column, columnIndex) => (
                                <th
                                    key={columnIndex}
                                    style={{width: 'auto'}}
                                    className={`${columns[columnIndex] === false ? 'import-unmatched' : ''}`}
                                >
                                    <Select
                                        value={columns[columnIndex]}
                                        placeholder={columns[columnIndex] === false ? 'Unmatched' : 'Choose...'}
                                        onChange={(event, newValue) => {
                                            // for some reason the select changes false to null without user interaction
                                            // so we override it here
                                            if(newValue === null) {
                                                return;
                                                // newValue = false;
                                            }
                                            let _columns = [...columns];
                                            _columns[columnIndex] = newValue;
                                            setColumns(_columns);

                                            // if the column to the left is an involvement column, try to automatch this one
                                            if(
                                                (newValue === 'involvement_date' || newValue === 'involvement_notes')
                                                && columnIndex > 0
                                                && _columns[columnIndex - 1]
                                                && !!additionalInformation?.columns?.[columnIndex - 1]
                                            ) {
                                                setAdditionalInformationForColumn(columnIndex, additionalInformation.columns[columnIndex - 1]);
                                            }
                                        }}
                                    >
                                    {Object.entries(importPossibleColumns).map(([optionName, option], optionIndex) => {
                                        return (
                                            <Option
                                                key={optionName}
                                                value={optionName}
                                                label={option.name}
                                                disabled={isFilterAlreadyAdded(optionName)}
                                            >
                                                {option.name}
                                            </Option>
                                        )
                                    })}
                                    </Select>
                                </th>
                            ))}
                            </tr>
                            {columnsHaveSubSelection() === true && (
                                <tr>
                                {previewRows?.[0]?.map((column, columnIndex) => (
                                <th
                                    key={columnIndex}
                                    style={{width: 'auto'}}
                                    className={`${columns[columnIndex] === false ? 'import-unmatched' : ''}`}
                                >
                                {typeof columns[columnIndex] === 'string' && columns[columnIndex].indexOf('involvement') !== -1 && (<>
                                    <SearchSelect
                                        endpoint={'involvements'}
                                        // hydrateWith={hydrateWith}
                                        loadAll={true}
                                        // multiple={restProps?.multiple}
                                        groupBy={(option) => option.category_name}
                                        value={additionalInformation?.columns?.[columnIndex]}
                                        onChange={(newVal) => {
                                            setAdditionalInformationForColumn(columnIndex, newVal);
                                        }}
                                    />
                                </>)}
                                </th>))}
                                </tr>
                            )}
                            </thead>
                            <tbody>
                            {previewRows?.slice(1)?.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row.map((column, columnIndex) => (
                                        <td
                                            key={columnIndex}
                                            style={{width: 'auto'}}
                                            className={`${columns[columnIndex] === '--ignore--' ? 'import-ignored' : ''} ${columns[columnIndex] === false ? 'import-unmatched' : ''}`}
                                        >
                                            {column}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            {totalLines > previewRows.length && (<tr>
                                <td colSpan={previewRows[0].length}>
                                    (and {formatNumber(totalLines - previewRows.length)} more...)
                                </td>
                            </tr>)}
                            </tbody>
                        </Table>
                        </Sheet>


                        <Controller
                            name={'has_headers'}
                            control={control}
                            render={({ field }) => (<>
                                <FormControl sx={{py: 2}}>
                                <Checkbox
                                    // {...field}
                                    label={'This file contains column headers'}
                                    checked={getValues('has_headers') === 1}
                                    onChange={() => {
                                        setValue('has_headers', getValues('has_headers') === 1 ? 0 : 1);
                                    }}
                                />
                                <FormHelperText><b>Warning:</b> This is advanced functionality. Only uncheck this option if you are absolutely sure of what you are doing.</FormHelperText>
                                </FormControl>
                            </>)}
                        />

                        <Typography sx={{fontSize: 'lg', fontWeight: 'bold', mb: 0.5}}>Final Review</Typography>
                        <Typography>Once you have reviewed the columns above and are ready to proceed, check the box below and then click the import button.</Typography>

                        <FormControl sx={{py: 2}}>
                            <Checkbox
                                label={'The columns are correct and I double-checked my work'}
                                disabled={hasUnselectedInvolvements() || hasDuplicatedInvolvements()}
                                checked={columnsCorrect}
                                onChange={() => {
                                    setColumnsCorrect(!columnsCorrect);
                                }}
                            />
                        </FormControl>

                        <Button
                            type="submit"
                            loading={isSaving}
                            disabled={!columnsCorrect || hasUnselectedInvolvements() || hasDuplicatedInvolvements()}
                        >
                            Import Data
                        </Button>
                        </>)}

                        {activeStep === 3 && (<>
                            <Typography sx={{fontSize: 'lg', fontWeight: 'bold', mb: 0.5}}>Your import is processing!</Typography>
                            <Typography>
                            Your data may take a while to process. You may <Link to={`/tools/importers/${importId}`}>view the status</Link> of your import.
                            </Typography>
                        </>)}
                    </Box>
                </AbstractObjectRelationship>
                </FormProvider>
            </form>
        </Box>
    </>);
}
